import ls from "local-storage";
import { pki } from "node-forge";
import { SIGNED_ATTRIBUTES, TokenInfo, TokenType } from "nxsec-shared";
import { toast } from "react-toastify";
import * as uuid from "uuid/v4";
import { Controller } from "./Controller";
import { UserController } from "./UserController";

class TokensController extends Controller {
  constructor() {
    super("tokens", {
      createNewToken: false,
      deleteToken: false,
      keyPassword: false,
      list: {},
      SGs: []
    });
    this.registerRoute({ path: "/tokens", strict: false, exact: true }, this.syncPendingTokens);
    this.registerRoute({ path: "/tokens/:token", strict: false, exact: true }, this.syncTokenSGs);
  }

  syncPendingTokens() {
    this.asyncAction("SYNC_TOKENS", async () => {
      let { pending, reviewed, tokens } = await this.ajax("/tokens");
      return { list: { pending, reviewed, tokens } || {} };
    });
  }

  syncTokenSGs({ token }) {
    this.asyncAction("SYNC_TOKEN_SGS", async () => {
      let SGs = await this.ajax(`/tokens/${token}/SGs`);
      return { SGs };
    });
  }
  afterSYNC_TOKEN_SGS(state) {
    this.syncPendingTokens();
    return state;
  }

  // This will approve a security approval pending query
  reviewToken(data: any, verb: "REVOKE" | "APPROVE") {
    let { TokenType, hash, Password } = data;
    let { login } = Controller.get<UserController>("users").getMe();
    delete data.hash;
    delete data.RequestType;
    delete data.Password;
    this.signToken(TokenType, data, login, Password).then(token => {
      if (token) {
        let action = `CONFIRM_TOKEN_${verb}D`;
        this.asyncAction(`${verb}_TOKEN`, async () => {
          return await this.ajax(
            `/token/${hash}/review`,
            "POST",
            //@ts-ignore
            { TokenType, Status: `${verb}D`, token },
            { action } // action is important to be triggered in case of async MFA
          ).then(r => {
            if (r && r.success) {
              Controller.dispatch({ type: action });
            }
          });
        });
      } else {
        toast.error("Token signature aborted! Please check your private key in your account's profile.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 10000
        });
      }
    });
  }

  approveToken(data: any) {
    this.reviewToken(data, "APPROVE");
  }
  onCONFIRM_TOKEN_APPROVED(state, { hash }) {
    toast.success(`Token successfully APPROVED`, {
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 10000
    });
    return { ...state, hash };
  }
  afterCONFIRM_TOKEN_APPROVED(state) {
    this.syncPendingTokens();
    return state;
  }

  revokeToken(data: any) {
    this.reviewToken(data, "REVOKE");
  }
  onCONFIRM_TOKEN_REVOKED(state, { hash }) {
    toast.success(`Token successfully REVOKED`, {
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 3000
    });
    return { ...state, hash };
  }
  afterCONFIRM_TOKEN_REVOKED(state) {
    this.syncPendingTokens();
    return state;
  }

  /*
   * This will delete a token
   **/
  deleteToken({ TokenType, hash }) {
    let action = `CONFIRM_TOKEN_DELETED`;
    this.asyncAction("DELETE_TOKEN", async () => {
      return await this.ajax(
        `/token/${hash}/review`,
        "POST",
        { TokenType, Status: "DELETED" },
        { action } // action is important to be triggered in case of async MFA
      ).then(r => {
        if (r && r.success) {
          Controller.dispatch({ type: action, hash });
        }
      });
    });
  }
  onCONFIRM_TOKEN_DELETED(state, { hash }) {
    toast.success(`Token successfully DELETED`, {
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 3000
    });
    return { ...state, hash };
  }
  afterCONFIRM_TOKEN_DELETED(state) {
    this.syncPendingTokens();
    return state;
  }

  /*
   * This will create a new security approval pending query
   **/
  submitToken(params: any) {
    let action = `CONFIRM_TOKEN_SUBMIT`,
      asyncResult;
    this.asyncAction(
      "SUBMIT_TOKEN",
      async () => {
        asyncResult = await this.ajax(
          `/token`,
          "POST",
          params,
          { action } // action is important to be triggered in case of async MFA
        ).catch(err => {
          toast.error(
            params.RequestType === "delete"
              ? `TokenID '${params.TokenId}' not found or unknown failure!`
              : `The token has not been submitted : unknown error`,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: 10000
            }
          );
        });
      },
      () => {
        if (asyncResult && asyncResult.success) {
          Controller.dispatch({ type: action, RequestType: params.RequestType });
        }
      }
    );
  }

  onCONFIRM_TOKEN_SUBMIT(state, action) {
    toast.success(`REQUEST for token successfully submitted`, {
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 3000
    });
    return {
      ...state,
      result: action.result
    };
  }

  afterCONFIRM_TOKEN_SUBMIT(state) {
    this.syncPendingTokens();
    return state;
  }

  // Security approval token dialog boxes
  showKeyPasswordDialog(action, tokenData) {
    Controller.dispatch({ type: "SHOW_PASSWORD_DIALOG", action, tokenData });
  }

  onSHOW_PASSWORD_DIALOG(state, { action, tokenData }) {
    return { ...state, keyPassword: { action, tokenData } };
  }

  hideKeyPasswordDialog() {
    Controller.dispatch({ type: "HIDE_PASSWORD_DIALOG" });
  }

  onHIDE_PASSWORD_DIALOG(state) {
    return { ...state, keyPassword: false };
  }

  showCreateNewTokenDialog() {
    Controller.dispatch({ type: "SHOW_CREATE_NEW_DIALOG" });
  }

  onSHOW_CREATE_NEW_DIALOG(state) {
    return { ...state, createNewToken: true };
  }

  hideCreateNewTokenDialog() {
    Controller.dispatch({ type: "HIDE_CREATENEW_DIALOG" });
  }

  onHIDE_CREATENEW_DIALOG(state) {
    return { ...state, createNewToken: false };
  }

  showDeleteTokenDialog() {
    Controller.dispatch({ type: "SHOW_DELETE_DIALOG" });
  }

  onSHOW_DELETE_DIALOG(state) {
    return { ...state, deleteToken: true };
  }

  hideDeleteTokenDialog() {
    Controller.dispatch({ type: "HIDE_DELETE_DIALOG" });
  }

  onHIDE_DELETE_DIALOG(state) {
    return { ...state, deleteToken: false };
  }

  /**
   * Build public key based on the privatekey in local-storage, or a new autogenerated-one with the password provided
   * @param {string} Password
   * @returns { publicKey, privateKey, autogenerated } - Returns the public and privatekey, autogenerated is TRUE when private key has been auto-generated
   */
  async genKeys(Password: string) {
    let PrivateKey: any = ls("privateKey"); // Getting PK from local storage
    if (PrivateKey && Password) {
      // Private key provided, generating public key from private key
      console.info(`Private key and password provided, using this key...`);
      try {
        let pk = pki.decryptRsaPrivateKey(PrivateKey, Password);
        if (!pk) {
          throw new Error(`Invalid private key or password`);
        }
        let pub = pki.setRsaPublicKey(pk.n, pk.e);
        let publicKey = pki.publicKeyToPem(pub);
        return { publicKey, privateKey: PrivateKey, autogenerated: false };
      } catch (err) {
        console.error(
          `The provided PrivateKey is not supported, please make sure this is a password encrypted PEM format: ${err.message}`
        );
        throw err;
      }
    } else {
      // Create keys
      let msg = `No private key provided, generating keys...`;
      console.info(msg);
      toast.warn(msg, {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 15000
      });

      // Generating using multithread mode to make it in a second, instead it takes dozens of seconds
      let { publicKey, privateKey } = await new Promise(resolve => {
        pki.rsa.generateKeyPair({ bits: 4096, workers: -1 }, (err, result) => {
          resolve(result);
        });
      });
      publicKey = pki.publicKeyToPem(publicKey);

      let encryptedPrivateKey = pki.encryptRsaPrivateKey(privateKey, Password);
      ls("privateKey", encryptedPrivateKey); // Storing PK in local storage

      toast.success(
        `PrivateKey autogenerated based on your password and stored into your browser's local storage (not any server).`,
        {
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 15000
        }
      );
      toast.warn(`Keep in mind this password for your next signatures.`, {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 15000
      });

      return { publicKey, privateKey: encryptedPrivateKey, autogenerated: true };
    }
  }

  /**
   * Get a UUID into a pseudo b64
   * Remove = complements
   * Replace + by _ and / by .
   */
  getBase64UUID() {
    let result = Buffer.from(uuid("binary"), "binary").toString("base64");
    result = result.replace(/=/g, "").replace(/\//g, ".").replace(/\+/g, "_");
    return result;
  }

  /**
   * Create a new token
   * @param type Type of token
   * @param data
   * @param user
   * @param password
   */
  async signToken(type: TokenType, data: TokenInfo, user: string, password: string) {
    try {
      let { publicKey, privateKey } = await this.genKeys(password);
      let token: any = {
        uuid: this.getBase64UUID(),
        type: type,
        data: JSON.stringify(data),
        signer: user,
        signedDate: new Date(),
        publicKey
      };
      let attr = {};
      const { createSign } = require("crypto");
      let sign = createSign("RSA-SHA256");
      SIGNED_ATTRIBUTES.forEach(k => (attr[k] = token[k]));
      sign.update(JSON.stringify(attr));
      token.signature = sign.sign({ key: privateKey, passphrase: password }, "hex");
      return token;
    } catch (e) {
      toast.error(e.message, {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 10000
      });
    }
  }
}
export { TokensController };
